import { useState, type FormEvent } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCredentialSave } from "@/lib/hooks/useCredentialSave";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { CredentialHealth, CredentialShell, SaveFeedback } from "./CredentialShell";

const ERRORS: Record<string, string> = {
  invalid_shein_sub_id: "Sub-id inválido.",
  tenant_not_found: "Conta não encontrada. Entre de novo no painel.",
};
const MAX_SUB_ID_LENGTH = 100;

interface Props {
  readonly credential: AffiliateCredentialPublic | undefined;
}

/** SHEIN: sub-id do afiliado; so converte URL canonica de produto. */
export function SheinCredentialSection({ credential }: Props): JSX.Element {
  const [subId, setSubId] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const { saving, status, error, save } = useCredentialSave("shein", ERRORS);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const normalized = subId.trim();
    if (!normalized || normalized.length > MAX_SUB_ID_LENGTH) {
      setLocalError(`Informe o sub-id (até ${MAX_SUB_ID_LENGTH} caracteres).`);
      return;
    }
    setLocalError(null);
    if (await save("upsert_shein_affiliate_credential", { p_sub_id: normalized })) {
      setSubId("");
    }
  };

  return (
    <CredentialShell id="shein" title="SHEIN">
      <div className="space-y-1.5">
        <p className="text-sm font-semibold text-foreground">Sub-id de afiliado</p>
        {credential && <p className="font-mono text-xs text-muted-foreground">{credential.tag_masked ?? credential.public_id}</p>}
        <p className="text-xs text-muted-foreground">
          A SHEIN só aceita o link da página do produto, que termina em -p-número.html. Links curtos, de campanha ou
          de busca mostram erro na hora de gerar.
        </p>
      </div>
      <CredentialHealth credential={credential} />

      <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="space-y-2">
          <Label htmlFor="shein-sub-id" variant="field">Sub-id</Label>
          <Input id="shein-sub-id" value={subId} onChange={(e) => setSubId(e.target.value)} placeholder="meu-subid" autoComplete="off" />
        </div>
        <Button type="submit" variant="gold" disabled={saving}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
          Salvar e testar
        </Button>
      </form>
      <SaveFeedback status={status} error={localError ?? error} />
    </CredentialShell>
  );
}
